import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Input } from '@highlight/ui'
import { parseTimeInput } from '../utils/parse-time-input'
import { formatUserTime, usePreferredHourCycle } from '../../utils/user-time-format'
import type { PreferredHourCycle } from '@shared/ipc/routers/BaseIpc.router'

/** Render a 24h "HH:MM" value in the user's hour cycle (e.g. "9:30 PM" / "21:30"). */
function formatDisplayTime(value: string, hourCycle: PreferredHourCycle | null): string {
  const match = value.match(/^(\d{2}):(\d{2})$/)
  if (!match) return value
  const date = new Date()
  date.setHours(parseInt(match[1], 10), parseInt(match[2], 10), 0, 0)
  return formatUserTime(date, hourCycle)
}

/** Only allow characters that can appear in a time expression. */
function sanitize(text: string): string {
  return text.replace(/[^0-9:.\sapmAPM]/g, '')
}

interface TimeFieldProps {
  value?: string
  onFocus: () => void
  onBlur?: () => void
  onChange: (time: string) => void
}

/**
 * A plain text input for entering a time. The native input owns the caret,
 * selection, paste and backspace — we only hold the in-progress text and parse
 * it on commit (blur / Enter).
 *
 * The draft holds exactly what the user typed (e.g. "9", "9:3", "430p"), so
 * intermediate states read naturally instead of being reformatted on every
 * keystroke. On commit we run the forgiving {@link parseTimeInput}; a parseable
 * value is emitted as 24h "HH:MM" via `onChange` and the display snaps to the
 * hour-cycle-aware format, while an unparseable value reverts to the last valid
 * `value`.
 */
export function TimeField({ value, onFocus, onBlur, onChange }: TimeFieldProps) {
  const hourCycle = usePreferredHourCycle()
  const [isFocused, setIsFocused] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  // The last value we emitted via onChange, so a commit never fires onChange for
  // an unchanged value — dedupes the Enter-then-blur double commit and the
  // no-op focus+blur, each of which would otherwise PATCH the staged action.
  const lastEmittedRef = useRef<string | undefined>(value)
  // True once the user has typed into this field but not yet committed, so the
  // value-sync effect below won't overwrite their in-progress text.
  const dirtyRef = useRef(false)

  // Re-sync the draft when the controlled `value` changes underneath the field —
  // e.g. a timeline drag, a 30m/1h preset, or the other field clamping mutate the
  // shared draft while a nested time popover stays open. Skipped only while the
  // user is ACTIVELY typing (dirty): a focused-but-untouched field then reflects
  // the external change (and won't clobber it on blur), while in-progress typing is
  // never lost. Typing itself never changes `value` (we only emit on commit).
  useEffect(() => {
    if (dirtyRef.current) return
    setDraft(value ? formatDisplayTime(value, hourCycle) : '')
    lastEmittedRef.current = value
  }, [value, hourCycle])

  // While editing, the local draft is the source of truth; when blurred the field
  // mirrors the controlled `value`.
  const display = isFocused ? draft : value ? formatDisplayTime(value, hourCycle) : ''

  const handleFocus = () => {
    setIsFocused(true)
    dirtyRef.current = false
    lastEmittedRef.current = value
    setDraft(value ? formatDisplayTime(value, hourCycle) : '')
    // Select all so a click-and-type replaces the existing time; positioning the
    // caret to edit a single digit still works via a subsequent click/arrow.
    requestAnimationFrame(() => inputRef.current?.select())
    onFocus()
  }

  const commit = () => {
    const parsed = parseTimeInput(draft)
    if (parsed) {
      // Only emit when the value actually changed (see lastEmittedRef).
      if (parsed !== lastEmittedRef.current) {
        lastEmittedRef.current = parsed
        onChange(parsed)
      }
      setDraft(formatDisplayTime(parsed, hourCycle))
    } else {
      // Revert to the last valid value (or empty if none).
      setDraft(value ? formatDisplayTime(value, hourCycle) : '')
    }
    // Committed (or reverted) — the draft is no longer in-progress.
    dirtyRef.current = false
  }

  const handleBlur = () => {
    commit()
    setIsFocused(false)
    onBlur?.()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      // commit() runs again on the resulting blur, but lastEmittedRef dedupes it.
      commit()
      inputRef.current?.blur()
    }
  }

  return (
    <Input
      ref={inputRef}
      size="sm"
      type="text"
      value={display}
      placeholder="Add Time"
      onFocus={handleFocus}
      onChange={(e) => {
        dirtyRef.current = true
        setDraft(sanitize(e.target.value))
      }}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      className="w-[120px] shrink-0"
    />
  )
}
